import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    InputNumber,
    message,
    Popconfirm,
    Space,
    Card,
    Typography,
    Row,
    Col,
    Tag,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function Features() {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);
    const [form] = Form.useForm();

    const categories = [
        { value: 'homepage', label: 'Homepage Features' },
        { value: 'service', label: 'Service Features' },
        { value: 'platform', label: 'Platform Features' },
        { value: 'general', label: 'General Features' },
    ];

    useEffect(() => {
        fetchFeatures();
    }, []);

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetFeatures();
            if (response.success) {
                setFeatures(response.data.data || []);
            }
        } catch (error) {
            message.error('Failed to fetch features');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingFeature) {
                await api.adminUpdateFeature(editingFeature.id, values);
                message.success('Feature updated successfully');
            } else {
                await api.adminCreateFeature(values);
                message.success('Feature created successfully');
            }
            setModalVisible(false);
            setEditingFeature(null);
            form.resetFields();
            fetchFeatures();
        } catch (error) {
            message.error('Failed to save feature');
        }
    };

    const handleEdit = (feature) => {
        setEditingFeature(feature);
        form.setFieldsValue({
            ...feature,
            is_active: feature.is_active,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteFeature(id);
            message.success('Feature deleted successfully');
            fetchFeatures();
        } catch (error) {
            message.error('Failed to delete feature');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center">
                    <span className="text-2xl mr-2">{record.icon}</span>
                    <div>
                        <div className="font-medium">{text}</div>
                        <div className="text-sm text-gray-500">{record.category}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (description) => (
                <div className="max-w-xs">
                    <div className="truncate">{description}</div>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => {
                const categoryInfo = categories.find(cat => cat.value === category);
                return (
                    <Tag color="blue">
                        <AppstoreOutlined className="mr-1" />
                        {categoryInfo?.label || category}
                    </Tag>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.is_active ? 'green' : 'red'}>
                    {record.is_active ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Sort Order',
            dataIndex: 'sort_order',
            key: 'sort_order',
            render: (order) => <span className="font-mono">{order}</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this feature?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>Features Management</Title>
                <p className="text-gray-600">
                    Manage platform features displayed on your homepage and other sections
                </p>
            </div>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <div>
                        <Title level={4} className="!mb-0">
                            All Features ({features.length})
                        </Title>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingFeature(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                    >
                        Add Feature
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={features}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} features`,
                    }}
                />
            </Card>

            <Modal
                title={editingFeature ? 'Edit Feature' : 'Add New Feature'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingFeature(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        category: 'homepage',
                        is_active: true,
                        sort_order: 0,
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Feature Name"
                        rules={[{ required: true, message: 'Please enter feature name' }]}
                    >
                        <Input placeholder="Enter feature name" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please enter feature description' }]}
                    >
                        <TextArea
                            rows={3}
                            placeholder="Enter feature description..."
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="icon"
                                label="Icon (Emoji)"
                            >
                                <Input placeholder="🍽️" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select category' }]}
                            >
                                <Select placeholder="Select category">
                                    {categories.map(category => (
                                        <Option key={category.value} value={category.value}>
                                            {category.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="image"
                        label="Image URL"
                    >
                        <Input placeholder="Enter image URL (optional)" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="is_active"
                                label="Active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="sort_order"
                                label="Sort Order"
                            >
                                <InputNumber min={0} placeholder="0" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="flex justify-end space-x-2">
                        <Button onClick={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingFeature ? 'Update' : 'Create'} Feature
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default Features;
